import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CountriesService } from 'src/app/services/countries/countries.service';
import { CountryModalService } from 'src/app/services/country-modal/country-modal.service';
import { PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

declare var bootstrap: any;

@Component({
  selector: 'app-countries',
  templateUrl: './countries.component.html',
  styleUrls: ['./countries.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class CountriesComponent extends DestructorComponent implements OnInit {

  countries: any[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';
  showFiltersForm: boolean = false;
  currentPage: number = 1;

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  // Pagination
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Filters
  filters: any = {
    search: '',
    date_from: '',
    date_to: ''
  };

  constructor(
    private _countriesService: CountriesService,
    private _countryModalService: CountryModalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.loadCountries();
  }

  setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.loadCountries(1, searchTerm, this.filters);
      })
    );
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  loadCountries(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;
    this.subscriptions.push(
      this._countriesService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.countries = response.data.data || [];

            // Update pagination info
            if (response.data.pagination) {
              this.paginationInfo = {
                currentPage: response.data.pagination.currentPage || page,
                perPage: response.data.pagination.perPage || 20,
                total: response.data.pagination.total || 0,
                lastPage: response.data.pagination.lastPage || 1,
                firstPage: 1
              };
            } else {
              this.resetPagination();
            }
          } else {
            this.countries = [];
            this.resetPagination();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading countries:', error);
          this.countries = [];
          this.resetPagination();
        }
      })
    );
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  onPageChange(page: number): void {
    this.loadCountries(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadCountries(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadCountries(1, '', {});
  }

  openAddCountryModal(): void {
    this._countryModalService.clearSelectedCountry();
    const modalElement = document.getElementById('kt_modal_country_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditCountryModal(country: any): void {
    this._countryModalService.setSelectedCountry(country);
    const modalElement = document.getElementById('kt_modal_country_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCountry(country: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete country "${country.name || 'this country'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(country);
      }
    });
  }

  private performDelete(country: any): void {
    const countryId = country._id || country.id;
    this.subscriptions.push(
      this._countriesService.DeleteDataById(countryId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Country has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCountries(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete country.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting country:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting the country.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onCountryAdded(): void {
    console.log('Country added, refreshing list...');
    this.loadCountries(this.currentPage, this.searchTerm, this.filters);
  }

  onCountryUpdated(): void {
    console.log('Country updated, refreshing list...');
    this.loadCountries(this.currentPage, this.searchTerm, this.filters);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }
}
